import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'

const CHANNEL_TYPES = ['telegram', 'discord', 'sms'] as const
type ChannelType = typeof CHANNEL_TYPES[number]

const CONFIG_FIELDS: Record<ChannelType, { key: string; label: string; placeholder: string }[]> = {
  telegram: [{ key: 'bot_token', label: 'Bot token', placeholder: '123456:ABC-...' }],
  discord: [{ key: 'bot_token', label: 'Bot token', placeholder: 'Discord bot token' }],
  sms: [
    { key: 'account_sid', label: 'Account SID', placeholder: 'AC...' },
    { key: 'auth_token', label: 'Auth token', placeholder: '' },
    { key: 'from_number', label: 'From number', placeholder: '+1...' },
  ],
}

export function Channels() {
  const { id } = useParams<{ id: string }>()
  const api = useAuth((s) => s.api)()
  const qc = useQueryClient()
  const [adding, setAdding] = useState<ChannelType | null>(null)
  const [configValues, setConfigValues] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['channels', id],
    queryFn: () => api.listChannels(id!),
    enabled: !!id,
  })

  async function activate() {
    if (!adding) return
    setSaving(true)
    try {
      await api.activateChannel(id!, adding, configValues)
      setAdding(null)
      setConfigValues({})
      qc.invalidateQueries({ queryKey: ['channels', id] })
    } finally {
      setSaving(false)
    }
  }

  async function deactivate(type: string) {
    await api.deactivateChannel(id!, type)
    qc.invalidateQueries({ queryKey: ['channels', id] })
  }

  const activeTypes = new Set(channels.filter((c) => c.active).map((c) => c.type))

  return (
    <div>
      <div className="cp-page-header">
        <div className="cp-page-subtitle">Messaging channels for this familiar</div>
        {!adding && (
          <div style={{ display: 'flex', gap: 8 }}>
            {CHANNEL_TYPES.filter((t) => !activeTypes.has(t)).map((t) => (
              <button key={t} className="cp-btn cp-btn--ghost" onClick={() => { setAdding(t); setConfigValues({}) }}>
                + {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {adding && (
        <div className="cp-card" style={{ marginBottom: 20 }}>
          <div className="cp-form">
            <div style={{ fontWeight: 600, textTransform: 'capitalize', marginBottom: 4 }}>{adding}</div>
            {CONFIG_FIELDS[adding].map(({ key, label, placeholder }) => (
              <div key={key} className="cp-field">
                <label className="cp-label">{label}</label>
                <input
                  className="cp-input"
                  placeholder={placeholder}
                  value={configValues[key] ?? ''}
                  onChange={(e) => setConfigValues((v) => ({ ...v, [key]: e.target.value }))}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="cp-btn cp-btn--ghost" onClick={() => setAdding(null)}>Cancel</button>
              <button className="cp-btn cp-btn--primary" onClick={activate} disabled={saving}>
                {saving ? 'Activating…' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && <p className="cp-empty">Loading…</p>}
      {!isLoading && channels.length === 0 && <p className="cp-empty">No channels activated yet.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {channels.map((c) => (
          <div key={c.id} className="cp-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{c.type}</span>
              <span className={`cp-badge ${c.active ? 'cp-badge--active' : 'cp-badge--draft'}`} style={{ marginLeft: 8 }}>
                {c.active ? 'active' : 'inactive'}
              </span>
            </div>
            {c.active && (
              <button className="cp-btn cp-btn--ghost" style={{ fontSize: 12 }} onClick={() => deactivate(c.type)}>
                Deactivate
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
